// TICKET-6 🔴 Fonction non testée : computePrestationModeResult (3e mode de calcul)
// Fonction non testée : getCalcMissionTemplatesByCategory
// Voir calcMission.test.ts pour les tests existants (14 tests)

import { CalcMissionSession, CalcMissionSimpleDraft, MissionCostPhase, TreasuryProfile, UserProfile } from '@shared/types';
import { generateId } from '@shared/utils';

export type CalcMissionTemplateCategory = 'universel' | 'business' | 'metier' | 'finance';
export type CalcMissionTier = 'free' | 'starter' | 'pro';

export interface CalcMissionTemplate {
  key: string;
  label: string;
  description: string;
  category: CalcMissionTemplateCategory;
  tier: CalcMissionTier;
  mode: 'simple' | 'scratchpad';
  simplePreset?: Partial<CalcMissionSimpleDraft>;
  content?: string;
  tags?: string[];
}

export interface CalcMissionMetric {
  key: string;
  label: string;
  value: number;
  hint: string;
}

export interface CalcMissionSimpleResult {
  revenue: number;
  directCosts: number;
  contingency: number;
  totalCosts: number;
  grossMargin: number;
  marginRate: number;
  dailyBreakEven: number;
  targetTjm: number;
  projectedNet: number;
  missionValueWithSafety: number;
  metrics: CalcMissionMetric[];
}

export interface MonthlyProjectionRow {
  month: number;
  label: string;
  days: number;
  revenue: number;
  fixedCosts: number;
  variableCosts: number;
  extraCosts: number;
  totalCosts: number;
  grossMargin: number;
  cumulativeMargin: number;
}

export interface CalcTjmModeResult {
  caEstime: number;
  netEstime: number;
  netParMois: number;
}

export interface CalcBudgetModeResult {
  tjmEquivalent: number;
  joursDisponibles: number;
  margeSecurite: number;
  revenusNet: number;
}

export function computeTjmModeResult(
  tjm: number,
  daysPerMonth: number,
  missionDuration: number,
  contingencyPct: number,
): CalcTjmModeResult {
  const caEstime = tjm * daysPerMonth * missionDuration;
  const netEstime = caEstime * (1 - contingencyPct / 100);
  const netParMois = missionDuration > 0 ? netEstime / missionDuration : 0;
  return { caEstime, netEstime, netParMois };
}

export function computeBudgetModeResult(
  totalBudget: number,
  missionDuration: number,
  daysPerMonth: number,
  contingencyPct: number,
): CalcBudgetModeResult {
  const joursDisponibles = daysPerMonth * missionDuration;
  const tjmEquivalent = joursDisponibles > 0 ? totalBudget / joursDisponibles : 0;
  const margeSecurite = totalBudget * (contingencyPct / 100);
  const revenusNet = totalBudget - margeSecurite;
  return { tjmEquivalent, joursDisponibles, margeSecurite, revenusNet };
}

export interface CalcPrestationModeResult {
  tjmEquivalent: number;
  prestationsParMois: number;
  caParMois: number;
  caTotal: number;
  joursParMois: number;
  netEstime: number;
}

export function computePrestationModeResult(
  tarifPrestation: number,
  joursParPrestation: number,
  daysPerMonth: number,
  missionDuration: number,
  contingencyPct: number,
): CalcPrestationModeResult {
  const prestationsParMois = joursParPrestation > 0 ? Math.floor(daysPerMonth / joursParPrestation) : 0;
  const joursParMois = prestationsParMois * joursParPrestation;
  const tjmEquivalent = joursParPrestation > 0 ? tarifPrestation / joursParPrestation : 0;
  const caParMois = prestationsParMois * tarifPrestation;
  const caTotal = caParMois * missionDuration;
  const netEstime = caTotal * (1 - contingencyPct / 100);
  return { tjmEquivalent, prestationsParMois, caParMois, caTotal, joursParMois, netEstime };
}

export interface CalcScratchpadLine {
  kind: 'value' | 'error';
  label: string;
  raw: string;
  value?: number;
  unit?: 'days' | 'currency' | 'ratio' | 'count';
  formattedValue?: string;
  message?: string;
}

export interface CalcScratchpadResult {
  variables: Record<string, number>;
  lines: CalcScratchpadLine[];
}

export const CALC_MISSION_CATEGORY_LABELS: Record<CalcMissionTemplateCategory, string> = {
  universel: 'Universel',
  business: 'Business',
  metier: 'Metiers',
  finance: 'Finance',
};

const NET_RATES: Record<TreasuryProfile['status'], number> = {
  ei: 0.78,
  sasu: 0.60,
  eurl: 0.55,
  portage: 0.50,
};

const DEFAULT_DAYS_PER_WEEK = 5;

function getMonthLabel(month: number): string {
  const labels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  return labels[(month - 1) % 12];
}

function computePhaseCosts(phase: MissionCostPhase, daysPerMonth: number, tjm: number, _month: number): { days: number; revenue: number; fixedCosts: number; variableCosts: number; extraCosts: number } {
  const phaseDaysPerWeek = phase.daysPerWeek || DEFAULT_DAYS_PER_WEEK;
  const phaseDaysPerMonth = daysPerMonth * (phaseDaysPerWeek / DEFAULT_DAYS_PER_WEEK);
  const days = Math.round(phaseDaysPerMonth);
  const revenue = tjm * days;
  return {
    days,
    revenue,
    fixedCosts: phase.fixedCosts,
    variableCosts: phase.variableCosts,
    extraCosts: phase.extraCosts,
  };
}

export function computeMonthlyProjection(draft: CalcMissionSimpleDraft): MonthlyProjectionRow[] {
  const { tjm, days: baseDaysPerMonth, fixedCosts, variableCosts, extraCosts, phases, missionDuration } = draft;
  const duration = missionDuration || 3;

  if (!phases || phases.length === 0) {
    const rows: MonthlyProjectionRow[] = [];
    let cumulative = 0;
    for (let m = 1; m <= duration; m++) {
      const revenue = tjm * baseDaysPerMonth;
      const totalCosts = fixedCosts + variableCosts + extraCosts;
      const grossMargin = revenue - totalCosts;
      cumulative += grossMargin;
      rows.push({
        month: m,
        label: getMonthLabel(m),
        days: baseDaysPerMonth,
        revenue,
        fixedCosts,
        variableCosts,
        extraCosts,
        totalCosts,
        grossMargin,
        cumulativeMargin: cumulative,
      });
    }
    return rows;
  }

  const rows: MonthlyProjectionRow[] = [];
  let cumulative = 0;
  for (let m = 1; m <= duration; m++) {
    const phase = phases.find((p) => m >= p.startMonth && m <= p.endMonth);
    if (phase) {
      const { days, revenue, fixedCosts: fc, variableCosts: vc, extraCosts: ec } = computePhaseCosts(phase, baseDaysPerMonth, tjm, m);
      const totalCosts = fc + vc + ec;
      const grossMargin = revenue - totalCosts;
      cumulative += grossMargin;
      rows.push({ month: m, label: phase.label, days, revenue, fixedCosts: fc, variableCosts: vc, extraCosts: ec, totalCosts, grossMargin, cumulativeMargin: cumulative });
    } else {
      const revenue = tjm * baseDaysPerMonth;
      const totalCosts = fixedCosts + variableCosts + extraCosts;
      const grossMargin = revenue - totalCosts;
      cumulative += grossMargin;
      rows.push({ month: m, label: getMonthLabel(m), days: baseDaysPerMonth, revenue, fixedCosts, variableCosts, extraCosts, totalCosts, grossMargin, cumulativeMargin: cumulative });
    }
  }
  return rows;
}

const CALC_MISSION_BASE_TEMPLATES: CalcMissionTemplate[] = [
  {
    key: 'regie-simple',
    label: 'Regie simple',
    description: 'Verifier rapidement un prix, des jours et une marge.',
    category: 'universel',
    tier: 'free',
    mode: 'simple',
    tags: ['regie', 'prix', 'jours'],
    simplePreset: { tjm: 650, days: 12, fixedCosts: 300, variableCosts: 120, contingencyPct: 10 },
  },
  {
    key: 'forfait-cadre',
    label: 'Forfait cadre',
    description: 'Poser un budget forfait avec marge et securite.',
    category: 'business',
    tier: 'starter',
    mode: 'simple',
    tags: ['forfait', 'budget'],
    simplePreset: { tjm: 720, days: 15, fixedCosts: 450, variableCosts: 200, contingencyPct: 15 },
  },
  {
    key: 'atelier-cdp',
    label: 'Atelier CDP',
    description: 'Estimer un atelier de cadrage ou de pilotage.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['cdp', 'atelier'],
    content: [
      '# Atelier cadrage / CDP',
      'tjm = 780',
      'jours_prepa = 2',
      'jours_ateliers = 3',
      'jours_restitution = 2',
      'jours_total = jours_prepa + jours_ateliers + jours_restitution',
      'budget = tjm * jours_total',
      'frais = 240',
      'marge = budget - frais',
    ].join('\n'),
  },
  {
    key: 'audit-ux',
    label: 'Audit UX',
    description: 'Construire un prix d audit et de restitution.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['ux', 'audit'],
    content: [
      '# Audit UX / design',
      'tjm = 690',
      'entretiens = 5',
      'cout_entretien = 45',
      'jours_audit = 4',
      'jours_reco = 2',
      'budget = tjm * (jours_audit + jours_reco)',
      'cout_recherche = entretiens * cout_entretien',
      'reste = budget - cout_recherche',
    ].join('\n'),
  },
  {
    key: 'deco-interieur',
    label: 'Decoratrice interieur',
    description: 'Estimer un accompagnement deco avec deplacements et achats.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['deco', 'chantier'],
    content: [
      '# Decoratrice interieur',
      'prix_jour = 520',
      'jours_conception = 3',
      'jours_suivi = 2',
      'deplacements = 180',
      'echantillons = 90',
      'budget = prix_jour * (jours_conception + jours_suivi)',
      'couts = deplacements + echantillons',
      'marge = budget - couts',
    ].join('\n'),
  },
  {
    key: 'rh-freelance',
    label: 'Mission RH freelance',
    description: 'Cadrez une mission RH ou recrutement avec un budget simple.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['rh', 'recrutement'],
    content: [
      '# Mission RH freelance',
      'tjm = 640',
      'jours_cadrage = 2',
      'jours_sourcing = 4',
      'jours_suivi = 2',
      'jours_total = jours_cadrage + jours_sourcing + jours_suivi',
      'budget = tjm * jours_total',
      'outils = 120',
      'marge = budget - outils',
    ].join('\n'),
  },
  {
    key: 'objectif-net',
    label: 'Objectif net mensuel',
    description: 'Retrouver un prix jour minimum a partir d un net vise.',
    category: 'finance',
    tier: 'free',
    mode: 'simple',
    tags: ['objectif', 'tjm'],
    simplePreset: { netTarget: 4000, days: 16, fixedCosts: 300, variableCosts: 100, contingencyPct: 8 },
  },
  {
    key: 'formation',
    label: 'Session de formation',
    description: 'Mesurer la rentabilite d une journee ou d un cycle de formation.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['formation', 'atelier'],
    content: [
      '# Session de formation',
      'prix_jour = 780',
      'jours_animation = 2 j',
      'jours_preparation = 1 j',
      'jours_suivi = 1 j',
      'jours_total = jours_animation + jours_preparation + jours_suivi',
      'budget = prix_jour * jours_total',
      'supports = 90',
      'deplacements = 120',
      'reste = budget - supports - deplacements',
    ].join('\n'),
  },
  {
    key: 'artisan-chantier',
    label: 'Artisan / chantier',
    description: 'Estimer un chantier avec temps, achats et marge de securite.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['artisan', 'chantier'],
    content: [
      '# Artisan / chantier',
      'prix_jour = 460',
      'jours_etude = 1 j',
      'jours_pose = 4 j',
      'jours_suivi = 1 j',
      'jours_total = jours_etude + jours_pose + jours_suivi',
      'budget = prix_jour * jours_total',
      'materiaux = 680',
      'deplacements = 90',
      'marge = budget - materiaux - deplacements',
    ].join('\n'),
  },
  {
    key: 'devops-cicd',
    label: 'Mission DevOps / CI-CD',
    description: 'Cadrer une mission infra avec run, ateliers et documentation.',
    category: 'metier',
    tier: 'pro',
    mode: 'scratchpad',
    tags: ['devops', 'infra'],
    content: [
      '# Mission DevOps / CI-CD',
      'tjm = 820',
      'jours_setup = 4 j',
      'jours_run = 6 j',
      'jours_documentation = 2 j',
      'jours_total = jours_setup + jours_run + jours_documentation',
      'budget = tjm * jours_total',
      'outils = 140',
      'reste = budget - outils',
    ].join('\n'),
  },
  {
    key: 'amoa-transfo',
    label: 'AMOA / transformation',
    description: 'Evaluer une mission d accompagnement avec ateliers et suivi.',
    category: 'metier',
    tier: 'pro',
    mode: 'scratchpad',
    tags: ['amoa', 'transformation'],
    content: [
      '# AMOA / transformation',
      'tjm = 760',
      'jours_cadrage = 3 j',
      'jours_ateliers = 4 j',
      'jours_suivi = 3 j',
      'jours_total = jours_cadrage + jours_ateliers + jours_suivi',
      'budget = tjm * jours_total',
      'frais = 180',
      'reste = budget - frais',
    ].join('\n'),
  },
  {
    key: 'photo-evenement',
    label: 'Photo / evenement',
    description: 'Estimer une mission photo avec prise de vue, tri et livraison.',
    category: 'metier',
    tier: 'starter',
    mode: 'scratchpad',
    tags: ['photo', 'event'],
    content: [
      '# Photo / evenement',
      'prix_jour = 540',
      'jours_prepa = 1 j',
      'jours_shoot = 1 j',
      'jours_postprod = 2 j',
      'jours_total = jours_prepa + jours_shoot + jours_postprod',
      'budget = prix_jour * jours_total',
      'location = 180',
      'livraison = 45',
      'reste = budget - location - livraison',
    ].join('\n'),
  },
  {
    key: 'retainer-mensuel',
    label: 'Accompagnement mensuel',
    description: 'Poser un forfait mensuel recurrent avec reserve et temps inclus.',
    category: 'business',
    tier: 'starter',
    mode: 'simple',
    tags: ['retainer', 'mensuel'],
    simplePreset: { tjm: 650, days: 4, fixedCosts: 150, variableCosts: 60, contingencyPct: 12, successRate: 100 },
  },
];

export function getCalcMissionTemplates(): CalcMissionTemplate[] {
  return CALC_MISSION_BASE_TEMPLATES;
}

export function canUseCalcMissionTemplate(template: CalcMissionTemplate, tier: CalcMissionTier): boolean {
  if (template.tier === 'free') return true;
  if (template.tier === 'starter') return tier === 'starter' || tier === 'pro';
  return tier === 'pro';
}

export function getCalcMissionTemplatesByCategory(tier: CalcMissionTier) {
  const categories: Record<CalcMissionTemplateCategory, { available: CalcMissionTemplate[]; locked: CalcMissionTemplate[] }> = {
    universel: { available: [], locked: [] },
    business: { available: [], locked: [] },
    metier: { available: [], locked: [] },
    finance: { available: [], locked: [] },
  };

  CALC_MISSION_BASE_TEMPLATES.forEach((template) => {
    if (canUseCalcMissionTemplate(template, tier)) {
      categories[template.category].available.push(template);
    } else {
      categories[template.category].locked.push(template);
    }
  });

  return categories;
}

export function getCalcMissionTemplate(key?: string | null): CalcMissionTemplate | undefined {
  if (!key) return undefined;
  return CALC_MISSION_BASE_TEMPLATES.find((template) => template.key === key);
}

export function createDefaultCalcSimpleDraft(profile?: UserProfile | null): CalcMissionSimpleDraft {
  return {
    name: '',
    client: '',
    pricingMode: undefined,
    tjm: profile?.tjm || 650,
    days: profile?.daysPerMonth || 10,
    fixedCosts: 250,
    variableCosts: 120,
    extraCosts: 0,
    netTarget: profile?.netTarget ?? 2500,
    successRate: 100,
    contingencyPct: 10,
    status: profile?.status || 'sasu',
    missionDuration: 3,
    totalBudget: 50000,
    tarifPrestation: 1500,
    joursPrestation: 2,
    phases: undefined,
  };
}

export function applyTemplateToSimpleDraft(
  draft: CalcMissionSimpleDraft,
  template: CalcMissionTemplate,
  profile?: UserProfile | null,
): CalcMissionSimpleDraft {
  return {
    ...createDefaultCalcSimpleDraft(profile),
    ...draft,
    ...template.simplePreset,
  };
}

export function createCalcMissionSession(options?: {
  name?: string;
  projectId?: string;
  profile?: UserProfile | null;
  templateKey?: string;
}): CalcMissionSession {
  const now = Date.now();
  const template = getCalcMissionTemplate(options?.templateKey);
  const simple = applyTemplateToSimpleDraft(createDefaultCalcSimpleDraft(options?.profile), template || {
    key: 'blank',
    label: 'Nouveau calcul',
    description: '',
    category: 'universel',
    tier: 'free',
    mode: 'simple',
  }, options?.profile);

  return {
    id: generateId(),
    projectId: options?.projectId,
    name: options?.name || template?.label || 'Nouveau calcul',
    mode: template?.mode || 'simple',
    templateKey: template?.key,
    simple,
    scratchpad: template?.content || '',
    createdAt: now,
    updatedAt: now,
  };
}

export function calculateNetFromRevenue(revenue: number, status: TreasuryProfile['status']): number {
  if (!Number.isFinite(revenue) || revenue <= 0) return 0;
  return revenue * (NET_RATES[status] || 0.6);
}

export function computeCalcMissionSimpleResult(draft: CalcMissionSimpleDraft): CalcMissionSimpleResult {
  const baseRevenue = draft.pricingMode === 'prestation' && draft.tarifPrestation && draft.joursPrestation
    ? computePrestationModeResult(draft.tarifPrestation, draft.joursPrestation, draft.days, 1, 0).caParMois
    : draft.tjm * draft.days;
  const revenue = baseRevenue * ((draft.successRate || 100) / 100);
  const directCosts = draft.fixedCosts + draft.variableCosts + draft.extraCosts;
  const contingency = revenue * ((draft.contingencyPct || 0) / 100);
  const totalCosts = directCosts + contingency;
  const grossMargin = revenue - totalCosts;
  const marginRate = revenue > 0 ? grossMargin / revenue : 0;
  const dailyBreakEven = draft.days > 0 ? totalCosts / draft.days : 0;
  const targetTjm = draft.days > 0 ? draft.netTarget / (NET_RATES[draft.status] || 0.6) / draft.days : 0;
  const projectedNet = calculateNetFromRevenue(Math.max(revenue - directCosts, 0), draft.status);
  const missionValueWithSafety = revenue + contingency;

  return {
    revenue,
    directCosts,
    contingency,
    totalCosts,
    grossMargin,
    marginRate,
    dailyBreakEven,
    targetTjm,
    projectedNet,
    missionValueWithSafety,
    metrics: [
      { key: 'budget', label: 'Budget estime', value: revenue, hint: 'Ce que la mission peut rapporter' },
      { key: 'costs', label: 'Couts a couvrir', value: totalCosts, hint: 'Charges directes et marge de securite' },
      { key: 'margin', label: 'Reste estime', value: grossMargin, hint: 'Ce qu il vous reste avant arbitrage fin' },
      { key: 'target', label: 'Prix / jour minimum', value: targetTjm, hint: 'Pour approcher votre net vise' },
    ],
  };
}

function splitTopLevelCommas(s: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of s) {
    if (char === '(') depth++;
    else if (char === ')') depth--;
    if (char === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

function replaceConditionalToTernary(s: string): string {
  const re = /(?:if|si)\s*\(/;
  let match = re.exec(s);
  while (match) {
    const start = match.index;
    const afterParen = start + match[0].length;
    let depth = 1;
    let pos = afterParen;
    while (depth > 0 && pos < s.length) {
      if (s[pos] === '(') depth++;
      else if (s[pos] === ')') depth--;
      pos++;
    }
    const argsStr = s.slice(afterParen, pos - 1);
    const args = splitTopLevelCommas(argsStr);
    if (args.length === 3) {
      s = s.slice(0, start) + `(${args[0]} ? ${args[1]} : ${args[2]})` + s.slice(pos);
    } else {
      s = s.slice(0, start) + s.slice(pos);
    }
    match = re.exec(s);
  }
  return s;
}

function sanitizeExpression(expression: string, variables: Record<string, number>): string {
  let s = expression.replace(/(\d),(\d)/g, '$1.$2');
  s = s
    .replace(/(\d+(?:\.\d+)?)\s*(€|eur|euros?)/gi, '$1')
    .replace(/(\d+(?:\.\d+)?)\s*(j|jour|jours)\b/gi, '$1')
    .replace(/(\d+(?:\.\d+)?)\s*%/g, '($1/100)')
    .replace(/(\d+(?:\.\d+)?)\s*(pct|pourcent)\b/gi, '($1/100)')
    .replace(/(\d+(?:\.\d+)?)\s*\/\s*j\b/gi, '$1');
  Object.entries(variables).forEach(([key, value]) => {
    s = s.replace(new RegExp(`\\b${key}\\b`, 'g'), String(value));
  });
  // Convert si()/if() function calls to ternary operators
  s = replaceConditionalToTernary(s);
  s = s.replace(/\btrue\b/gi, '1');
  s = s.replace(/\bfalse\b/gi, '0');
  s = s.replace(/\bnot\b/gi, '!');
  s = s.replace(/\band\b/gi, '&&');
  s = s.replace(/\bor\b/gi, '||');
  if (!/^[0-9+\-*/()._%?:,\s<>!=&|A-Za-z_.]+$/.test(s)) throw new Error('Expression non prise en charge');
  return s;
}

function prettyLabel(name: string): string {
  return name.replace(/_/g, ' ').trim();
}

function inferUnit(name: string): CalcScratchpadLine['unit'] {
  const normalized = name.toLowerCase();

  if (/(^|_)(taux|ratio|part|pct|pourcent|success)(_|$)/i.test(normalized)) return 'ratio';
  if (
    /(^|_)(tjm|prix|budget|ca|cout|couts|co[uû]ts|frais|marge|reste|revenu|revenus|location|materiaux|deplacements|outils|supports|livraison|net|brut|salaire|commission|benefice|dividendes?|epargne|besoin|equivalent|runway|forfait|base|bas|reserves?|cotisations?|is)(_|$)/i.test(normalized)
  ) {
    return 'currency';
  }
  if (/(^|_)(jour|jours|day|days)(_|$)/i.test(normalized)) return 'days';
  if (/(^|_)(nb|nombre|quantite|echantillons|entretiens|tests|participants|sessions)(_|$)/i.test(normalized)) return 'count';
  return 'count';
}

function formatScratchpadValue(value: number, unit: CalcScratchpadLine['unit']): string {
  if (unit === 'days') return `${value} j`;
  if (unit === 'currency') return `${Math.round(value).toLocaleString('fr-FR')} €`;
  if (unit === 'ratio') return `${Math.round(value * 100)} %`;
  return `${Math.round(value * 100) / 100}`;
}

const SCRATCHPAD_INDENT_WIDTH = 2;

type ScratchpadSourceLine = {
  rawLine: string;
  indent: number;
  content: string;
};

type ScratchpadStatement =
  | {
      kind: 'assignment';
      rawLine: string;
      variableName: string;
      expression: string;
    }
  | {
      kind: 'conditional-assignment';
      rawLine: string;
      condition: string;
      variableName: string;
      expression: string;
    }
  | {
      kind: 'if';
      rawLine: string;
      condition: string;
      thenBranch: ScratchpadStatement[];
      elseBranch: ScratchpadStatement[];
    }
  | {
      kind: 'error';
      rawLine: string;
      message: string;
    };

function toScratchpadSourceLine(rawLine: string): ScratchpadSourceLine {
  const leading = rawLine.match(/^[\t ]*/)?.[0] || '';
  const indent = Array.from(leading).reduce((count, char) => count + (char === '\t' ? SCRATCHPAD_INDENT_WIDTH : 1), 0);
  return {
    rawLine,
    indent,
    content: rawLine.slice(leading.length).trimEnd(),
  };
}

function isIgnorableScratchpadLine(line: ScratchpadSourceLine): boolean {
  const trimmed = line.content.trim();
  return !trimmed || trimmed.startsWith('#') || trimmed.startsWith('//') || trimmed.startsWith('*');
}

function evaluateScratchpadExpression(expression: string, variables: Record<string, number>): number {
  const sanitized = sanitizeExpression(expression, variables);
  const value = Function(
    'min',
    'max',
    'sum',
    'avg',
    `"use strict"; return (${sanitized});`,
  )(
    Math.min,
    Math.max,
    (...values: number[]) => values.reduce((acc, value) => acc + value, 0),
    (...values: number[]) => (values.length === 0 ? 0 : values.reduce((acc, value) => acc + value, 0) / values.length),
  ) as number;
  if (!Number.isFinite(value)) throw new Error('Resultat invalide');
  return value;
}

function evaluateScratchpadCondition(condition: string, variables: Record<string, number>): boolean {
  const sanitized = sanitizeExpression(condition, variables);
  const value = Function(
    'min',
    'max',
    'sum',
    'avg',
    `"use strict"; return (${sanitized});`,
  )(
    Math.min,
    Math.max,
    (...values: number[]) => values.reduce((acc, value) => acc + value, 0),
    (...values: number[]) => (values.length === 0 ? 0 : values.reduce((acc, value) => acc + value, 0) / values.length),
  );
  return Boolean(value);
}

function parseScratchpadStatements(lines: ScratchpadSourceLine[], startIndex: number, minIndent: number): { statements: ScratchpadStatement[]; nextIndex: number } {
  const statements: ScratchpadStatement[] = [];
  let index = startIndex;

  while (index < lines.length) {
    const current = lines[index];
    if (isIgnorableScratchpadLine(current)) {
      index += 1;
      continue;
    }

    if (current.indent < minIndent) break;

    const content = current.content.trim();
    const inlineIf = content.match(/^(?:if|si)\s+(.+?)[;:]\s*([a-zA-Z_]\w*)\s*=\s*(.+)$/);
    if (inlineIf) {
      const [, condition, variableName, expression] = inlineIf;
      statements.push({
        kind: 'conditional-assignment',
        rawLine: current.rawLine,
        condition: condition.trim(),
        variableName,
        expression: expression.trim(),
      });
      index += 1;
      continue;
    }

    const blockIf = content.match(/^(?:if|si)\s+(.+?)[;:]\s*$/);
    if (blockIf) {
      const condition = blockIf[1].trim();
      const ifIndent = current.indent;
      const thenResult = parseScratchpadStatements(lines, index + 1, ifIndent + 1);
      let nextIndex = thenResult.nextIndex;
      let elseBranch: ScratchpadStatement[] = [];

      while (nextIndex < lines.length && isIgnorableScratchpadLine(lines[nextIndex])) {
        nextIndex += 1;
      }

      const maybeElse = lines[nextIndex];
      if (maybeElse && maybeElse.indent === ifIndent && /^(?:else|sinon)[;:]$/.test(maybeElse.content.trim())) {
        const elseResult = parseScratchpadStatements(lines, nextIndex + 1, ifIndent + 1);
        elseBranch = elseResult.statements;
        nextIndex = elseResult.nextIndex;
      }

      statements.push({
        kind: 'if',
        rawLine: current.rawLine,
        condition,
        thenBranch: thenResult.statements,
        elseBranch,
      });
      index = nextIndex;
      continue;
    }

    const assignment = content.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*(.+)$/);
    if (assignment) {
      const [, variableName, expression] = assignment;
      statements.push({
        kind: 'assignment',
        rawLine: current.rawLine,
        variableName,
        expression,
      });
      index += 1;
      continue;
    }

    statements.push({ kind: 'error', rawLine: current.rawLine, message: 'Instruction non prise en charge' });
    index += 1;
  }

  return { statements, nextIndex: index };
}

function runScratchpadStatements(statements: ScratchpadStatement[], variables: Record<string, number>, lines: CalcScratchpadLine[]) {
  statements.forEach((statement) => {
    if (statement.kind === 'if') {
      const branch = evaluateScratchpadCondition(statement.condition, variables) ? statement.thenBranch : statement.elseBranch;
      runScratchpadStatements(branch, variables, lines);
      return;
    }
    if (statement.kind === 'conditional-assignment') {
      const condition = evaluateScratchpadCondition(statement.condition, variables);
      const previousValue = variables[statement.variableName] ?? 0;
      const value = condition ? evaluateScratchpadExpression(statement.expression, variables) : previousValue;
      variables[statement.variableName] = value;
      const unit = inferUnit(statement.variableName);
      lines.push({
        kind: 'value',
        label: prettyLabel(statement.variableName),
        raw: statement.rawLine,
        value,
        unit,
        formattedValue: formatScratchpadValue(value, unit),
      });
      return;
    }
    if (statement.kind === 'error') {
      lines.push({
        kind: 'error',
        label: 'Erreur',
        raw: statement.rawLine,
        message: statement.message,
      });
      return;
    }

    try {
      const value = evaluateScratchpadExpression(statement.expression, variables);
      variables[statement.variableName] = value;
      const unit = inferUnit(statement.variableName);
      lines.push({
        kind: 'value',
        label: prettyLabel(statement.variableName),
        raw: statement.rawLine,
        value,
        unit,
        formattedValue: formatScratchpadValue(value, unit),
      });
    } catch (error) {
      lines.push({
        kind: 'error',
        label: prettyLabel(statement.variableName),
        raw: statement.rawLine,
        message: error instanceof Error ? error.message : 'Erreur de calcul',
      });
    }
  });
}

export function runCalcScratchpad(source: string, systemVars?: Record<string, number>): CalcScratchpadResult {
  const variables: Record<string, number> = { ...systemVars };
  const lines: CalcScratchpadLine[] = [];
  const statements = parseScratchpadStatements(source.split('\n').map(toScratchpadSourceLine), 0, 0).statements;
  runScratchpadStatements(statements, variables, lines);

  return { variables, lines };
}
